import { useEffect, useMemo, useRef, useState } from 'react';
import { JsonEditor } from 'peak-editor-components';
import { Globe, Lock, Server } from 'lucide-react';

interface VariableDefinition {
  name: string;
  type: string;
  sourceNodeId: string;
  sourceNodeLabel: string;
  description?: string;
  example?: any;
}

interface RestConfigTabProps {
  data: any;
  onChange: (key: string, value: any) => void;
  context?: VariableDefinition[];
}

export const HttpTab: React.FC<RestConfigTabProps> = ({ data, onChange, context = [] }) => {
  const service = data.service || '';
  const endpoint = data.endpoint || '';
  const method = data.method || 'GET';
  const authType = data.authentication || 'none';
  const requestBody = data.requestBody || '{}';

  const headers = useMemo(() => JSON.stringify(data.headers || {}, null, 2), [data.headers]);
  const [headersDraft, setHeadersDraft] = useState(headers);
  const lastEmittedHeadersRef = useRef(headers);

  useEffect(() => {
      if (headers !== lastEmittedHeadersRef.current) {
          setHeadersDraft(headers);
      }
  }, [headers]);

  // Build variables object from process variable context
  const variablesObj = useMemo(() => {
      const vars: Record<string, any> = {};
      context.forEach(v => {
          vars[v.name] = v.example ?? `\${${v.name}}`;
      });
      return Object.keys(vars).length > 0 ? vars : { baseUrl: 'https://api.example.com', token: 'xyz' };
  }, [context]);

  const editorProps = useMemo(() => ({
      variables: variablesObj,
      variablesJson: JSON.stringify(variablesObj, null, 2),
      onVariablesChange: () => {},
      variableError: null,
      functions: [],
      onFunctionsChange: () => {},
  }), [variablesObj]);

  const handleBodyChange = (val: string) => onChange('requestBody', val);

  const handleHeadersChange = (val: string) => {
      setHeadersDraft(val);
      try {
          const parsed = JSON.parse(val);
          lastEmittedHeadersRef.current = JSON.stringify(parsed, null, 2);
          onChange('headers', parsed);
      } catch {
          // Ignore parse errors while user is typing
      }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 p-4 overflow-y-auto">
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6">

          {/* Request Line */}
          <div className="grid grid-cols-6 gap-4">
              <div className="col-span-1">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Method</label>
                  <select
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                      value={method}
                      onChange={e => onChange('method', e.target.value)}
                  >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                      <option value="DELETE">DELETE</option>
                  </select>
              </div>
              <div className="col-span-3">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2"><Server size={12}/> Service</label>
                  <input
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      value={service}
                      onChange={e => onChange('service', e.target.value)}
                      placeholder="peak://Peak.Service.Identity"
                  />
              </div>
              <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2"><Globe size={12}/> Endpoint URL</label>
                  <input
                      className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      value={endpoint}
                      onChange={e => onChange('endpoint', e.target.value)}
                      placeholder="/api/Company"
                  />
              </div>
          </div>

          {/* Authentication */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex items-center gap-2 mb-4">
                  <Lock size={16} className="text-slate-400"/>
                  <h4 className="text-sm font-bold text-slate-700">Authentication</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Auth Type</label>
                      <select
                          className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none"
                          value={authType}
                          onChange={e => onChange('authentication', e.target.value)}
                      >
                          <option value="none">None</option>
                          <option value="basic">Basic Auth</option>
                          <option value="bearer">Bearer Token</option>
                          <option value="api-key">API Key</option>
                      </select>
                  </div>
                  {authType === 'bearer' && (
                      <div>
                          <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Token</label>
                          <input
                              type="password"
                              className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none font-mono"
                              value={data.bearerToken || ''}
                              onChange={e => onChange('bearerToken', e.target.value)}
                              placeholder="Bearer Token"
                          />
                      </div>
                  )}
                  {authType === 'basic' && (
                      <div className="col-span-2 grid grid-cols-2 gap-4">
                          <input className="border p-2 rounded text-sm" placeholder="Username" value={data.username || ''} onChange={e => onChange('username', e.target.value)} />
                          <input className="border p-2 rounded text-sm" placeholder="Password" type="password" value={data.password || ''} onChange={e => onChange('password', e.target.value)} />
                      </div>
                  )}
                  {authType === 'api-key' && (
                      <div className="col-span-2">
                          <input className="w-full border p-2 rounded text-sm font-mono" placeholder="API Key" value={data.apiKey || ''} onChange={e => onChange('apiKey', e.target.value)} />
                      </div>
                  )}
              </div>
          </div>

          {/* Result Variable */}
          <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Result Variable</label>
              <input
                  type="text"
                  className="w-full border border-slate-300 rounded-lg p-2 text-sm outline-none font-mono"
                  value={data.resultVariable || ''}
                  onChange={e => onChange('resultVariable', e.target.value)}
                  placeholder="httpResult"
              />
              <p className="text-[10px] text-slate-400 mt-1">Process variable to store the response envelope.</p>
          </div>

          {/* Body & Headers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-96">
              <div className="flex flex-col">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Request Body (JSON)</label>
                  <div className="flex-1 border border-slate-300 rounded-lg overflow-hidden">
                      <JsonEditor
                          content={requestBody}
                          onChange={handleBodyChange}
                          {...editorProps}
                      />
                  </div>
              </div>
              <div className="flex flex-col">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Headers (JSON)</label>
                  <div className="flex-1 border border-slate-300 rounded-lg overflow-hidden">
                      <JsonEditor
                          content={headersDraft}
                          onChange={handleHeadersChange}
                          {...editorProps}
                      />
                  </div>
              </div>
          </div>
      </div>
    </div>
  );
};
